import axios from "axios";
import { message } from "antd";
import config from "./config";

// Helper to decode JWT and check expiration
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Check if token is already expired (no buffer - let server handle near-expiry)
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    // If we can't decode, let the request proceed and server will validate
    return false;
  }
};

// Helper to clear auth and redirect to login
const handleAuthFailure = (reason) => {
  console.error("Auth failure:", reason);

  // Clear all auth data
  localStorage.removeItem("auth_token");
  localStorage.removeItem("auth_email");
  localStorage.removeItem("auth_name");
  localStorage.removeItem("auth_avatar");

  // Clear React Query cache
  if (window.queryClient) {
    window.queryClient.clear();
  }

  // Show user-friendly message
  message.error({
    content: "Your session has expired. Please log in again.",
    key: "session-expired",
    duration: 3,
  });

  // Reload to trigger login screen (after a brief delay for message to show)
  setTimeout(() => {
    window.location.href = "/login";
  }, 1500);
};

// Validate and ensure API base URL is absolute
let apiBaseUrl = config.SPRING_API_BASE;
if (!apiBaseUrl) {
  console.error("SPRING_API_BASE is not configured!");
  apiBaseUrl = "https://rewixx-backend.wittygrass-81d5c888.eastus2.azurecontainerapps.io";
}

// Ensure baseURL is absolute (starts with http:// or https://)
if (!apiBaseUrl.startsWith('http://') && !apiBaseUrl.startsWith('https://')) {
  console.warn("API base URL is relative, converting to absolute:", apiBaseUrl);
  apiBaseUrl = window.location.origin + (apiBaseUrl.startsWith('/') ? '' : '/') + apiBaseUrl;
}

// Warn if baseURL matches current origin (would cause relative URL issues)
if (apiBaseUrl === window.location.origin || apiBaseUrl.startsWith(window.location.origin + '/')) {
  console.error("API base URL matches current origin - this will cause relative URL issues!");
  console.error("API Base URL:", apiBaseUrl);
  console.error("Current Origin:", window.location.origin);
}

// Create axios instance with default configuration
const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to validate URLs and log for debugging
api.interceptors.request.use(
  (request) => {
    // Ensure the final URL is absolute
    const fullUrl = request.baseURL 
      ? (request.baseURL.endsWith('/') ? request.baseURL.slice(0, -1) : request.baseURL) + 
        (request.url.startsWith('/') ? request.url : '/' + request.url)
      : request.url;
    
    // Check if URL is relative (doesn't start with http)
    if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
      console.error("Relative URL detected in API request!", {
        baseURL: request.baseURL,
        url: request.url,
        fullUrl: fullUrl
      });
    }
    
    // Warn if request is going to static site instead of API
    if (fullUrl.includes(window.location.origin) && !fullUrl.includes('/api/')) {
      console.warn("API request appears to be going to static site:", fullUrl);
    }
    
    return request;
  },
  (error) => Promise.reject(error)
);

// Attach JWT token from localStorage if available
api.interceptors.request.use(
  (request) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      // Only block if token is clearly expired - otherwise let server validate
      if (isTokenExpired(token)) {
        console.warn("Token expired, clearing auth");
        // Just clear the token, don't block the request - let 401 handler deal with it
        localStorage.removeItem("auth_token");
      } else {
        request.headers.Authorization = `Bearer ${token}`;
      }
    }
    return request;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle 401 errors
let isHandling401 = false; // Prevent multiple simultaneous 401 handlers

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isHandling401) {
      isHandling401 = true;
      console.error("401 Unauthorized - Token may be invalid or expired");
      console.error("Request URL:", error.config?.url);

      const token = localStorage.getItem("auth_token");
      if (token) {
        handleAuthFailure("401 response from server");
      }

      // Reset flag after a delay
      setTimeout(() => {
        isHandling401 = false;
      }, 2000);
    }
    return Promise.reject(error);
  }
);

class Backend {
  /**
   * Helper method to build URL with query parameters
   */
  static buildUrl(path, params = null) {
    // Ensure path doesn't start with / to avoid relative URL issues
    // Services already provide paths like "api/users/customers/list"
    let url = path.replace(/^\//, "");
    
    if (params) {
      const queryString = new URLSearchParams(params).toString();
      url += `?${queryString}`;
    }
    return url;
  }

  static async get(path, params = null) {
    try {
      const url = this.buildUrl(path, params);
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("GET Error:", error);
      throw error;
    }
  }

  static async post(path, body = null, params = null) {
    try {
      const url = this.buildUrl(path, params);
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      console.error("POST Error:", error);
      throw error;
    }
  }

  static async put(path, body = null, params = null) {
    try {
      const url = this.buildUrl(path, params);
      const response = await api.put(url, body);
      return response.data;
    } catch (error) {
      console.error("PUT Error:", error);
      throw error;
    }
  }

  static async delete(path, params = null) {
    try {
      const url = this.buildUrl(path, params);
      const response = await api.delete(url);
      return response.data;
    } catch (error) {
      console.error("DELETE Error:", error);
      throw error;
    }
  }
}

export default Backend;